import React, { useCallback, useMemo } from 'react';

import { CollegeFollowCard } from '../follows/CollegeFollowCard';

import { FollowersIcon } from 'components/svg/dashboard/FollowersIcon';
import { StatisticContainer } from '../StatisticContainer';
import { gql } from 'urql';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';

import { useAppState } from 'client/providers/AppProvider';
import {
    Espin_Follow_Type_Enum,
    FollowDocument,
    FollowMutation,
    FollowMutationVariables,
    GetFollowersForCollegesDocument,
    GetFollowersForCollegesQuery,
    GetFollowersForCollegesQueryVariables,
    UnfollowDocument,
    UnfollowMutation,
    UnfollowMutationVariables,
} from '../../../generated/spin-graphql';
gql`
    query GetFollowersForColleges($collegeId: uuid!, $date: timestamptz!) {
        my_follows: espin_follow(
            where: {
                college_id: { _eq: $collegeId }
                follow_type: { _in: [college_follows_player, college_follows_high_school] }
            }
        ) {
            high_school_id
            player_user_id
        }
        espin_follow(
            where: {
                college_id: { _eq: $collegeId }
                follow_type: { _eq: player_follows_college }
                player_user: { deleted_at: { _is_null: true } }
            }
        ) {
            ...CollegeFollowCard
        }
        espin_follow_aggregate(
            where: {
                college_id: { _eq: $collegeId }
                follow_type: { _in: [player_follows_college, high_school_follows_college] }
                created_at: { _gt: $date }
            }
        ) {
            aggregate {
                count
            }
        }
        high_school_follows: espin_follow(
            where: { college_id: { _eq: $collegeId }, follow_type: { _eq: high_school_follows_college } }
        ) {
            ...CollegeFollowCard
        }
        espin_graduating_class {
            grad_class_years
        }
    }
`;

type Props = {};
const backUpDate = new Date().toISOString();
export const CollegeFollowers: React.FC<Props> = ({}) => {
    const { user } = useAppState();
    const { isReady, data } = useAuthenticatedQuery<
        GetFollowersForCollegesQuery,
        GetFollowersForCollegesQueryVariables
    >(
        {
            query: GetFollowersForCollegesDocument,
            variables: {
                collegeId: user?.college_profile?.college.id,
                date: user.last_login ?? backUpDate,
            },
            pause: !user,
        },
        ['espin_follow'],
    );

    const years = useMemo(() => data?.espin_graduating_class?.[0].grad_class_years.split(',').map((s) => s.trim()), [
        data?.espin_graduating_class,
    ]);

    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);

    const [_, follow] = useAuthenticatedMutation<FollowMutation, FollowMutationVariables>(FollowDocument);

    // TODO make this unfollow not redirect to the players page? feels super awkward
    const handleUnfollowClicked = useCallback(
        (userId: string, followType: Espin_Follow_Type_Enum) => {
            unFollow(
                {
                    where: {
                        college_id: { _eq: user?.college_profile?.college.id },
                        follow_type: { _eq: followType },
                        ...(followType !== Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            player_user_id: { _eq: userId },
                        }),
                        ...(followType === Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            high_school_id: { _eq: userId },
                        }),
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [unFollow, user?.college_profile?.college?.id],
    );

    const handleFollowClicked = useCallback(
        (userId: string, followType: Espin_Follow_Type_Enum) => {
            follow(
                {
                    input: {
                        college_id: user?.college_profile?.college.id,
                        college_user_name: `${user?.college_profile?.first_name} ${user?.college_profile?.last_name}`,
                        ...(followType !== Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            player_user_id: userId,
                        }),
                        ...(followType === Espin_Follow_Type_Enum.CollegeFollowsHighSchool && {
                            high_school_id: userId,
                        }),
                        follow_type: followType,
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [follow, user?.college_profile],
    );

    const handleFollowOrUnfollow = useCallback(
        (userId: string, following: boolean, followType: Espin_Follow_Type_Enum) => {
            if (following) {
                handleUnfollowClicked(userId, followType);
            } else {
                handleFollowClicked(userId, followType);
            }
        },
        [handleFollowClicked, handleUnfollowClicked],
    );
    const listOfFollows = useMemo(() => data?.my_follows.map((f) => f.player_user_id ?? f.high_school_id), [
        data?.my_follows,
    ]);

    return (
        <>
            {isReady && (
                <div className="follow-container">
                    <StatisticContainer
                        label={'Total Followers'}
                        icon={<FollowersIcon />}
                        value={`${data.espin_follow.length + data.high_school_follows.length}`}
                    />
                    <StatisticContainer
                        label={'New Followers'}
                        icon={<FollowersIcon />}
                        value={`${data?.espin_follow_aggregate.aggregate.count}`}
                    />
                    <CollegeFollowCard
                        listOfFollows={listOfFollows}
                        followers={data.espin_follow}
                        hsFollowers={data.high_school_follows}
                        next4Years={years}
                        fakeCardLabel={'Follower'}
                        fakeCardSubLabel={'Upgrade to Premium to see your followers!'}
                        noContentIcon={<FollowersIcon />}
                        noContentLabel={'You have no followers'}
                        handleFollowButtonClicked={(
                            userId: string,
                            following: boolean,
                            followType: Espin_Follow_Type_Enum,
                        ) => handleFollowOrUnfollow(userId, following, followType)}
                    />
                </div>
            )}
        </>
    );
};
